// src/hooks/useAdminGroup.ts
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { IGroup } from 'src/services/IAM/AdminGroup/AdminGroup.interface'
import {
  resetGroup,
  updateGroup,
  setGroup,
} from 'src/redux/IAM/AdminGroups/adminGroupsSlice'
import {
  creategroup,
  deleteGroup,
  getGroups,
  updateGroup as updateGroupService,
} from 'src/services/IAM/AdminGroup/AdminGroup.service'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'

/**
 * Hook `useAdminGroup` for managing admin groups in the application. 
 * Provides functionality for CRUD operations including loading, creating, updating, and deleting groups.
 * Integrates with Redux for state management and shows alerts for user feedback.
 *
 * @returns An object containing:
 * - `groups`: List of all groups from the Redux store.
 * - `group`: The currently selected group.
 * - `updatedGroup`: The last updated group.
 * - `createGroupAdmin`: Function to create a new group.
 * - `setGroupAdmin`: Function to set the current group for editing.
 * - `loadGroups`: Function to load a paginated list of groups.
 * - `setUpdateGroup`: Function to set the updated group in the store.
 * - `removeGroup`: Function to delete a group by its ID.
 * - `clearGroups`: Function to clear the current group state in the Redux store.
 *
 * @example
 * // Example usage in a React component
 * const {
 *   groups,
 *   loadGroups,
 *   createGroupAdmin,
 *   setGroupAdmin,
 *   removeGroup,
 *   clearGroups,
 * } = useAdminGroup();
 *
 * useEffect(() => {
 *   loadGroups();
 * }, []);
 */
export const useAdminGroup = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { group, groups, updatedGroup } = useSelector(
    (state: RootState) => state.groups,
  )

  /**
   * Loads a paginated list of groups from the server.
   * Displays success or error alerts based on the result of the operation.
   *
   * @param {Object} params - Pagination parameters.
   * @param {number} [params.currentPage=1] - The current page to load.
   * @param {number} [params.pageSize=25] - The number of items per page.
   */
  const loadGroups = ({
    currentPage = 1,
    pageSize = 25,
  }: {
    currentPage?: number
    pageSize?: number
  } = {}) => {
    try {
      startLoading()
      dispatch(getGroups({ currentPage, pageSize }))
        .then((result: any) => {
          stopLoading()
          if (getGroups.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar los grupos',
            )
          } else if (getGroups.fulfilled.match(result)) {
            showAlert('success', 'Grupos cargados correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al cargar los permisos')
    }
  }

  /**
   * Creates a new group with the provided data.
   *
   * @param {IGroup} group - The data for the new group.
   */
  const createGroupAdmin = (group: IGroup) => {
    dispatch(creategroup(group))
      .then((result: any) => {
        stopLoading()
        if (creategroup.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al crear el grupo',
          )
        } else if (creategroup.fulfilled.match(result)) {
          loadGroups({ currentPage: 1, pageSize: 25 })
          showAlert('success', 'Grupos creados correctamente')
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Updates an existing group with the provided data.
   *
   * @param {IGroup} group - The updated data for the group.
   */
  const updatedGroupAdmin = (group: IGroup) => {
    dispatch(updateGroupService(group))
      .then((result: any) => {
        stopLoading()
        if (updateGroupService.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al actualizar el grupo',
          )
        } else if (updateGroupService.fulfilled.match(result)) {
          showAlert('success', 'Grupo actualizado correctamente')
          loadGroups()
          clearGroups()
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Deletes a group by its ID.
   *
   * @param {number} groupId - The ID of the group to delete.
   */
  const removeGroup = (groupId: number) => {
    dispatch(deleteGroup(groupId))
      .then((result: any) => {
        stopLoading()
        if (deleteGroup.rejected.match(result)) {
          showAlert(
            'error',
            String(result.payload) || 'Error al eliminar el grupo',
          )
        } else if (deleteGroup.fulfilled.match(result)) {
          showAlert('success', 'Grupo eliminado correctamente')
          loadGroups()
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Sets the updated group in the Redux state.
   *
   * @param {IGroup} group - The group data to set as updated.
   */
  const setUpdateGroup = (group: IGroup) => {
    dispatch(updateGroup(group))
  }

  /**
   * Sets the current group in the Redux state for editing.
   *
   * @param {IGroup} group - The group data to set.
   */
  const setGroupAdmin = (group: IGroup) => {
    dispatch(setGroup(group))
  }

  /**
   * Clears the current group state in the Redux store.
   */
  const clearGroups = () => {
    dispatch(resetGroup())
  }

  return {
    groups,
    group,
    updatedGroup,
    createGroupAdmin,
    setGroupAdmin,
    loadGroups,
    setUpdateGroup,
    removeGroup,
    clearGroups,
    updatedGroupAdmin,
  }
}
